<script lang="ts">
  import clsx from 'clsx';
  import { paddings } from '@athena-lib/spacing';
  import { Link } from '@athena-lib/Link';
  import { Image } from '@athena-lib/Image';
  import { Video } from '@athena-lib/Video';
  import { getMediaType } from '@athena-lib/asset-utils';
  import { cardColors, type CardProps } from '../types';

  let {
    children,
    class: className,
    backgroundMedia,
    gradientColor = 'dark',
    shadow = true,
    innerShadow,
    link,
    rounded = true,
    useMinHeight = true,
    backgroundColor = 'light',
    aspect = '2/3',
    padding = 'gutter',
    action = () => undefined,
    actionArg,
  }: CardProps = $props();

  const contentClasses = $derived.by(() => {
    const classes = [
      'w-full',
      'h-full',
      'relative',
      paddings[padding],
      {
        ['rounded-2xl']: rounded,
      },
    ];

    if (backgroundMedia) {
      classes.push('flex flex-col justify-end pointer-events-none');
    }

    return classes;
  });

  const innerShadowClasses = $derived.by(() => {
    const classes = [];
    if (innerShadow || (innerShadow === undefined && backgroundMedia)) {
      classes.push(
        'bg-gradient-to-b from-transparent',
        gradientColor === 'dark' ? 'to-st-black' : 'to-st-white',
      );
    }
    return classes;
  });

  function elementAction(node: HTMLDivElement) {
    if (action) {
      return action(node, actionArg!);
    }
  }

  const mediaTypeIs = $derived(getMediaType(backgroundMedia?.url));
  const cardTransitionClasses = 'hover:scale-105 transition-transform duration-[2440ms]';
  const assetClasses = 'object-cover z-0 w-full h-full';
</script>

{#snippet anchorIfLink(anchorLink: string | undefined, isExternal: boolean | undefined)}
  {#if anchorLink}
    <Link
      href={anchorLink}
      {isExternal}
      class={clsx('outline-4 outline-blue-500', rounded && 'rounded-2xl')}
    >
      {@render card()}
    </Link>
  {:else}
    {@render card()}
  {/if}
{/snippet}

{#snippet card()}
  <div
    use:elementAction
    class={clsx(
      'relative w-full overflow-hidden',
      useMinHeight && 'min-h-80',
      aspect === '2/3' && 'aspect-2/3',
      aspect === '4/5' && 'aspect-4/5',
      aspect === 'square' && 'aspect-square',
      cardColors[backgroundColor],
      backgroundMedia && 'z-0',
      {
        ['rounded-2xl']: rounded,
        ['shadow-card']: shadow,
      },
      className,
    )}
  >
    {#if backgroundMedia}
      {#if mediaTypeIs.image}
        <Image
          imgClass={clsx(
            backgroundMedia?.focalPoint === 'center' && 'object-center',
            backgroundMedia?.focalPoint === 'left' && 'object-left',
            backgroundMedia?.focalPoint === 'right' && 'object-right',
            cardTransitionClasses,
            assetClasses,
            'absolute',
            {
              ['rounded-2xl']: rounded,
            },
          )}
          loading="lazy"
          src={backgroundMedia.url}
          alt={backgroundMedia.alt}
        />
      {:else if mediaTypeIs.video}
        <Video
          class={clsx(cardTransitionClasses, assetClasses, {
            ['rounded-2xl']: rounded,
          })}
          src={backgroundMedia.url}
          alt={backgroundMedia.alt}
          autoplay={true}
          loop={true}
          muted={true}
        />
      {/if}
    {/if}
    {#if innerShadowClasses.length}
      <div class={clsx('absolute bottom-0 left-0 right-0 z-0 h-1/2', innerShadowClasses)}></div>
    {/if}
    <div class={clsx(contentClasses)}>
      {@render children()}
    </div>
  </div>
{/snippet}

{@render anchorIfLink(link?.href, link?.isExternal)}
